import React, { useEffect, useState } from 'react';
import useExperienceData from '../../../../hooks/useExperienceData';
import { BookingQuestion } from "../../../../types/bookingQuestions";
import { FormInput, FormSelect } from './FormComponents';
import { Experience } from '../../../../context/dataExperienceContext';
import { useTravelers } from '../../../../context/travelersContext';
import { getAvailableTransferModes, getFilteredPickupLocations, isConditionallyRequired, shouldSkipQuestionBasedOnArrivalMode } from './question-helpers';

type TravelerPickup = Experience['product']['logistics']['travelerPickup'];

interface PickUpQuestionProps {
  question: BookingQuestion;
  value: string | number;
  selectedArrivalMode: string;
  travelerPickup: TravelerPickup,
  onChange: (questionId: string, value: { question: string, answer: string, unit?: string, travelerNum?: number }) => void;
  commonProps: {
    id: string;
    label: string;
    value: string;
    required: boolean;
    hint?: string;
  };
}

const PickUpQuestion: React.FC<PickUpQuestionProps> = ({
  question,
  selectedArrivalMode,
  onChange,
  travelerPickup,
  commonProps
}) => {

  const [value, setValue] = useState<string>('')
  const [freeTextValue, setFreeTextValue] = useState<string>('')

  const onOptionSelection = (value: string) => {
    setValue(value)
    if (value === 'CONTACT_SUPPLIER_LATER') {
      onChange(question.id, {
        question: "PICKUP_POINT",
        answer: "CONTACT_SUPPLIER_LATER",
        unit: "LOCATION_REFERENCE"
      })
    } else if(value === 'FREETEXT') {
      setValue(value);
    } else {
      onChange(question.id, {
        question: "PICKUP_POINT",
        answer: value,
        unit: "LOCATION_REFERENCE"
      })
    }    
  }

  const onFreeTextUpdate = (value: string) => {
    setFreeTextValue(value)
    onChange(question.id, {
      question: 'PICKUP_POINT',
      answer: value,
      unit: 'FREETEXT'
    })
  }

  const { experience } = useExperienceData();
  const { selectedItemCode } = useTravelers();

  const filteredLocations = getFilteredPickupLocations(selectedArrivalMode, question, travelerPickup);
  const allowCustom = travelerPickup?.allowCustomTravelerPickup;

  const freeTextOption = [{ value: "FREETEXT", label: "I don't see my pick location" }];
  const contactSupplierLaterOption = [{ value: "CONTACT_SUPPLIER_LATER", label: "Contact supplier later" }];

  const contactSuplierLater = travelerPickup?.locations?.find(location => location.location.ref === 'CONTACT_SUPPLIER_LATER')
  const options = [
    { value: "", label: "Seleccionar ubicación", disabled: true },
    ...filteredLocations.map(location => ({ label: location.location.data.address, value: location.location.ref })),
    ...(allowCustom ? freeTextOption : []),
    ...(contactSuplierLater ? contactSupplierLaterOption : []),
  ]

  const selectedProductOption = experience?.product.productOptions.find(product => product.productOptionCode === selectedItemCode)
  const productIncludesPickup = selectedProductOption?.description?.includes('Pickup included') // Es increible que esto funcione asi https://partnerresources.viator.com/travel-commerce/merchant/implementing-booking-questions/
  const isPickupOfferedByThisProductOption = (
    (travelerPickup?.pickupOptionType === 'PICKUP_EVERYONE') ||
    (travelerPickup?.pickupOptionType === 'PICKUP_AND_MEET_AT_START_POINT' && productIncludesPickup)
  )
  
  if (question.id === 'PICKUP_POINT' && !isPickupOfferedByThisProductOption) {
    onChange(question.id, {
      question: 'PICKUP_POINT',
      answer: 'MEET_AT_DEPARTURE_POINT',
      unit: 'LOCATION_REFERENCE',
    });
    return null;
  }

  return (
    <div className="form-grid">
      <FormSelect
        {...commonProps}
        value={value}
        onChange={(e) => onOptionSelection(e.target.value)}
        options={options}
      />
      {value === 'FREETEXT' && allowCustom &&
        <FormInput
          {...commonProps}
          value={freeTextValue}
          onChange={(e) => onFreeTextUpdate(e.target.value)}
          type="text"
          placeholder={question.label}
          maxLength={question.maxLength}
        />
      }
    </div>
  );
};

interface QuestionProps {
  question: BookingQuestion;
  questions: BookingQuestion[];
  travelerNum: number | undefined,
  value: string | number;
  selectedArrivalMode: string;
  selectedDepartureMode: string;
  setSelectedArrivalMode: (value: string) => void;
  setSelectedDepartureMode: (value: string) => void;
  onChange: (questionId: string, value: { question: string, answer: string, unit?: string, travelerNum?: number }) => void;
  travelerPickup: TravelerPickup
}

const Question: React.FC<QuestionProps> = ({
  question,
  questions,
  travelerNum,
  value,
  onChange,
  selectedArrivalMode,
  selectedDepartureMode,
  setSelectedArrivalMode,
  setSelectedDepartureMode,
  travelerPickup
}) => {
  const [selectedUnit, setSelectedUnit] = useState<string | undefined>();

  useEffect(() => {
    const units = question.units;
    if (units) {
      if (units.length > 0) {
        setSelectedUnit(units[0])
      }
    }


  }, [question])

  const isRequired = question.required === "MANDATORY" ||
    isConditionallyRequired(question.id, selectedArrivalMode, selectedDepartureMode, travelerPickup, questions);

  const commonProps = {
    id: question.id,
    label: question.label,
    key: question.id,
    value: String(value),
    required: isRequired,
    hint: question.hint
  };

  if (shouldSkipQuestionBasedOnArrivalMode(question.id, selectedArrivalMode, selectedDepartureMode)) {
    return null;
  }

  const transferModeOptions = {
    AIR: "Avión",
    RAIL: "Tren",
    SEA: "Barco",
    OTHER: "Otro"
  };

  if ("TRANSFER_ARRIVAL_MODE" === question.id) {
    const availableModes = getAvailableTransferModes(question.id, questions || []);
    return (
      <FormSelect
        {...commonProps}
        value={selectedArrivalMode}
        onChange={(e) => {
          setSelectedArrivalMode(e.target.value);
          onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })
        }}
        options={[
          { value: "", label: "Seleccionar una opción", disabled: true },
          ...availableModes.map(mode => ({
            value: mode,
            label: transferModeOptions[mode as keyof typeof transferModeOptions] || mode
          }))
        ]}
      />
    );
  }

  if ("TRANSFER_DEPARTURE_MODE" === question.id) {
    const availableModes = getAvailableTransferModes(question.id, questions || []);
    return (
      <FormSelect
        {...commonProps}
        value={selectedDepartureMode}
        onChange={(e) => {
          setSelectedDepartureMode(e.target.value)
          onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })
        }}
        options={[
          { value: "", label: "Seleccionar una opción", disabled: true },
          ...availableModes.map(mode => ({
            value: mode,
            label: transferModeOptions[mode as keyof typeof transferModeOptions] || mode
          }))
        ]}
      />
    );
  }


  if (question.id === 'AGEBAND') {
    // Check https://partnerresources.viator.com/travel-commerce/merchant/implementing-booking-questions/
    // This kind of question is automatically answered based on travelers selection.
    return null;
  }


  /** 
   * The TRANSFER_ARRIVAL_DROP_OFF and TRANSFER_DEPARTURE_PICKUP questions are related
   * to locations (arrival drop off and departure pickup) however there are no specific
   * location references or conditions that apply to these questions (see the table).
   * Therefore customers should be provided with a freetext input field for these questions
   * and the answers must include the FREETEXT designation if the customer answered freely, via text.
   **/
  if (question.id === 'TRANSFER_ARRIVAL_DROP_OFF' || question.id === 'TRANSFER_DEPARTURE_PICKUP') {
    return (
      <FormInput
        {...commonProps}
        onChange={(e) => onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })}
        type="text"
        placeholder={question.label}
        maxLength={question.maxLength}
      />
    )
  }

  if (question.id === 'PICKUP_POINT') {
    return (
      <PickUpQuestion
        question={question}
        value={value}
        selectedArrivalMode={selectedArrivalMode}
        onChange={onChange}
        travelerPickup={travelerPickup}
        commonProps={commonProps}
      />
    );
  }

  switch (question.type) {
    case "STRING":
      return (<FormInput
        {...commonProps}
        onChange={(e) => onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })}
        type="text"
        placeholder={question.label}
        maxLength={question.maxLength}
      />)
    // Weight and Height
    case "NUMBER_AND_UNIT":
      return question.units && question.units.length > 0 ? (
            <FormInput
              {...commonProps}
              onChange={(e) => onChange(question.id, { question: question.id, answer: e.target.value, unit: selectedUnit, travelerNum: travelerNum })}
              type="number"
              placeholder="Value"
              unitsSelector={
              <FormSelect
                id={`${question.id}-unit`}
                value={selectedUnit || ''}
                onChange={(e) => setSelectedUnit(e.target.value)}
                options={question.units.map(unit => ({
                  value: unit,
                  label: unit
                }))}
              />}
            />
      ) : (
        <FormInput
          {...commonProps}
          onChange={(e) => onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })}
          type="number"
          placeholder={question.label}
        />
      );
    case "DATE":
      return (
        <FormInput
          {...commonProps}
          onChange={(e) => onChange(question.id, e.target.value)}
          type="date"
        />
      );

    case "TIME":
      return (
        <FormInput
          {...commonProps}
          onChange={(e) => onChange(question.id, e.target.value)}
          type="time"
        />
      );
    case "LOCATION_REF_OR_FREE_TEXT":
      // Handle other LOCATION_REF_OR_FREE_TEXT fields
      return question.allowedAnswers && question.allowedAnswers.length > 0 ? (
        <FormSelect
          {...commonProps}
          onChange={(e) => onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })}
          options={[
            { value: "", label: "Seleccionar una opcion", disabled: true },
            ...question.allowedAnswers.map(answer => ({
              value: answer,
              label: answer
            }))
          ]}
        />
      ) : (
        <FormInput
          {...commonProps}
          onChange={(e) => onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })}
          type="text"
          placeholder={question.label}
        />
      );

    default:
      return (
        <FormInput
          {...commonProps}
          onChange={(e) => onChange(question.id, { answer: e.target.value, question: question.id, travelerNum: travelerNum })}
          type="text"
          placeholder={question.label}
        />
      );
  }
};

export default Question;
